import { Navigate, Outlet, useLocation } from "react-router";
import { EmptyState } from "@fortex/map";
import type { IconName } from "@fortex/map";
import { AppBar, adminPaths } from "../app/AppMenu";
import { useApp, useAuth } from "../app/context";
import { features } from "../auth/store";
import type { AppLabels } from "../i18n";

export type AdminSection = { path: string; feature: string; labelKey: keyof AppLabels; icon: IconName };

export const adminSections: AdminSection[] = [
  { path: adminPaths.masterData, feature: features.masterData, labelKey: "menuMasterData", icon: "database" },
  { path: adminPaths.maps, feature: features.masterData, labelKey: "menuMaps", icon: "tiles" },
  { path: adminPaths.layers, feature: features.masterData, labelKey: "menuLayers", icon: "layers" },
  { path: adminPaths.users, feature: features.userAdmin, labelKey: "menuUsers", icon: "users" },
  { path: adminPaths.apiKeys, feature: features.userAdmin, labelKey: "menuApiKeys", icon: "login" },
  { path: adminPaths.publicMaps, feature: features.publicMap, labelKey: "menuPublicMaps", icon: "link" }
];

export function allowedSections(can: (feature: string, level: "ReadOnly" | "Full") => boolean): AdminSection[] {
  return adminSections.filter(a => can(a.feature, "ReadOnly"));
}

export function sectionOf(pathname: string): AdminSection | null {
  return adminSections.find(a => a.path === pathname) ?? adminSections.find(a => pathname.startsWith(a.path)) ?? null;
}

export function AdminHome() {
  const { labels } = useApp();
  const firstPath = useAuth(a => allowedSections(a.can)[0]?.path ?? null);

  if (firstPath === null) return <EmptyState icon="warning" title={labels.accessDenied} />;

  return <Navigate to={firstPath} replace />;
}

export function AdminLayout() {
  const { labels } = useApp();
  const location = useLocation();
  const current = sectionOf(location.pathname);

  return (
    <div className="fxapp-frame fxapp-admin" data-fxmap="admin">
      <AppBar title={current !== null ? (labels[current.labelKey] as string) : labels.adminTitle} icon={current?.icon ?? null} />
      <div className="fxapp-admin-content">
        <Outlet />
      </div>
    </div>
  );
}
